import React from 'react';
import { userPreferenceApi } from './api';

export const MULTI_MONITOR_PREF_NAMESPACE = 'viewer.multi_monitor_profiles';
export const MULTI_MONITOR_STORAGE_KEY = 'kairos.multiMonitorProfiles.v1';
export const MULTI_MONITOR_CHANNEL_PREFIX = 'kairos.viewer.workspace';

const DEFAULT_PROFILE_ID = 'default';

const safeJson = (raw, fallback) => {
  try { return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
};

const getWindowId = () => {
  if (typeof window === 'undefined') return 'server';
  const key = 'kairos.multiMonitor.windowId';
  let id = window.sessionStorage.getItem(key);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    window.sessionStorage.setItem(key, id);
  }
  return id;
};



export const parseLaunchIntent = (search = null) => {
  const rawSearch = search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const params = new URLSearchParams(rawSearch || '');
  const requested = (params.get('launchWorkspace') || params.get('autoLaunchWorkspace') || params.get('externalLaunch') || '').toLowerCase();
  const display = params.get('display') || (['1', 'true', 'yes'].includes((params.get('fullscreen') || '').toLowerCase()) ? 'clinical-fullscreen' : '');
  return {
    shouldLaunchWorkspace: ['1', 'true', 'yes', 'workspace', 'fullscreen'].includes(requested),
    profileId: params.get('launchProfile') || params.get('profile') || '',
    display,
    fullscreen: ['1', 'true', 'yes'].includes((params.get('fullscreen') || '').toLowerCase()) || display === 'clinical-fullscreen',
  };
};

export const resolveLaunchProfile = (loadedProfiles, launchIntent = {}) => {
  const profiles = Array.isArray(loadedProfiles?.profiles) ? loadedProfiles.profiles : [];
  if (!profiles.length) return null;
  return profiles.find(p => p.id === launchIntent.profileId)
    || profiles.find(p => p.id === loadedProfiles.activeProfileId)
    || profiles[0];
};

export const getWorkspaceParams = () => {
  if (typeof window === 'undefined') return { workspaceId: 'default', monitorId: 'primary', role: 'primary' };
  const params = new URLSearchParams(window.location.search);
  return {
    workspaceId: params.get('workspace') || 'default',
    monitorId: params.get('monitor') || 'primary',
    role: params.get('role') || (params.get('monitor') ? 'diagnostic' : 'primary'),
  };
};

const currentScreenDescriptor = () => {
  if (typeof window === 'undefined') return {};
  return { width: window.screen?.width || null, height: window.screen?.height || null, devicePixelRatio: window.devicePixelRatio || 1 };
};

const currentPlacement = () => {
  if (typeof window === 'undefined') return {};
  return { left: window.screenX || 0, top: window.screenY || 0, width: window.outerWidth || null, height: window.outerHeight || null };
};

export const createDefaultProfile = (overrides = {}) => ({
  id: DEFAULT_PROFILE_ID,
  name: 'Default diagnostic workspace',
  version: 1,
  updatedAt: new Date().toISOString(),
  workstationFingerprint: '',
  monitors: [
    {
      id: 'primary',
      label: 'Primary viewer',
      role: 'primary',
      descriptor: currentScreenDescriptor(),
      placement: currentPlacement(),
      viewportLayout: { rows: 2, cols: 2 },
      matchHints: [],
    },
    {
      id: 'diagnostic-2',
      label: 'Diagnostic monitor 2',
      role: 'comparison',
      descriptor: {},
      placement: {},
      viewportLayout: { rows: 2, cols: 2 },
      matchHints: [],
    },
  ],
  hangingProtocolBindings: {},
  ...overrides,
});

export const normalizeProfiles = (value) => {
  if (!value) return { activeProfileId: DEFAULT_PROFILE_ID, profiles: [createDefaultProfile()] };
  const profiles = Array.isArray(value.profiles) ? value.profiles : [];
  const normalized = profiles.length ? profiles : [createDefaultProfile()];
  return {
    activeProfileId: value.activeProfileId || normalized[0].id || DEFAULT_PROFILE_ID,
    profiles: normalized.map(p => ({ ...createDefaultProfile({ id: p.id || DEFAULT_PROFILE_ID, name: p.name || 'Workspace profile' }), ...p })),
  };
};

export const loadLocalProfiles = () => normalizeProfiles(safeJson(window.localStorage.getItem(MULTI_MONITOR_STORAGE_KEY), null));
export const saveLocalProfiles = (value) => {
  const normalized = normalizeProfiles(value);
  window.localStorage.setItem(MULTI_MONITOR_STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
};

export const loadProfiles = async () => {
  const local = loadLocalProfiles();
  try {
    const pref = await userPreferenceApi.get(MULTI_MONITOR_PREF_NAMESPACE);
    if (pref && Object.prototype.hasOwnProperty.call(pref, 'value')) {
      return saveLocalProfiles(pref.value);
    }
  } catch (e) {
    if (e?.response?.status === 404) {
      try { await userPreferenceApi.save(MULTI_MONITOR_PREF_NAMESPACE, local); } catch (_) {}
    }
  }
  return local;
};

export const saveProfiles = async (value) => {
  const normalized = saveLocalProfiles(value);
  try { await userPreferenceApi.save(MULTI_MONITOR_PREF_NAMESPACE, normalized); } catch (_) {}
  return normalized;
};


const normalizeTopologyScreen = (screen = {}) => ({
  label: screen.label || '',
  width: screen.width || screen.availWidth || '',
  height: screen.height || screen.availHeight || '',
  left: screen.left ?? screen.availLeft ?? '',
  top: screen.top ?? screen.availTop ?? '',
  isPrimary: !!screen.isPrimary,
});

export const computeMonitorTopologyFingerprint = ({ screens = [], platform = null, devicePixelRatio = null } = {}) => {
  const normalized = (screens || []).map(normalizeTopologyScreen)
    .sort((a, b) => `${a.left},${a.top},${a.width},${a.height},${a.label}`.localeCompare(`${b.left},${b.top},${b.width},${b.height},${b.label}`));
  const dpr = devicePixelRatio ?? (typeof window !== 'undefined' ? window.devicePixelRatio : 1);
  const plat = platform ?? (typeof navigator !== 'undefined' ? navigator.platform : 'unknown');
  const monitorPart = normalized.map(s => `${s.width}x${s.height}@${s.left},${s.top}${s.isPrimary ? ':primary' : ''}`).join('|');
  return `platform=${plat || 'unknown'};dpr=${dpr || 1};count=${normalized.length};monitors=${monitorPart}`;
};

const fingerprintCount = (fingerprint = '') => {
  const match = String(fingerprint || '').match(/count=(\d+)/);
  return match ? Number(match[1]) : null;
};

export const detectMonitorTopologyMismatch = (profile = null, { screens = [], platform = null, devicePixelRatio = null } = {}) => {
  if (!profile || !Array.isArray(screens) || screens.length === 0) {
    return { mismatched: false, savedFingerprint: profile?.workstationFingerprint || '', currentFingerprint: '', savedCount: profile?.monitors?.length || 0, currentCount: 0 };
  }
  const currentFingerprint = computeMonitorTopologyFingerprint({ screens, platform, devicePixelRatio });
  const savedFingerprint = profile.workstationFingerprint || '';
  const savedCount = fingerprintCount(savedFingerprint) ?? (profile.monitors || []).length;
  const currentCount = screens.length;
  return {
    mismatched: !!savedFingerprint && savedFingerprint !== currentFingerprint,
    savedFingerprint,
    currentFingerprint,
    savedCount,
    currentCount,
  };
};

export const describeScreen = (screen) => ({
  label: screen.label || '',
  width: screen.width,
  height: screen.height,
  availWidth: screen.availWidth,
  availHeight: screen.availHeight,
  left: screen.left,
  top: screen.top,
  availLeft: screen.availLeft,
  availTop: screen.availTop,
  isPrimary: !!screen.isPrimary,
  isInternal: !!screen.isInternal,
  devicePixelRatio: window.devicePixelRatio || 1,
});

const screenIdentity = (screen = {}) => [
  screen.label || '',
  screen.left,
  screen.top,
  screen.width,
  screen.height,
  screen.availLeft,
  screen.availTop,
  screen.availWidth,
  screen.availHeight,
].map(value => value === undefined || value === null ? '' : String(value)).join('|');

const uniqueScreens = (screens = []) => {
  const seen = new Set();
  return screens.filter((screen) => {
    const key = screenIdentity(screen);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const detectWindowPlacementSupport = () => ({
  broadcastChannel: typeof window !== 'undefined' && 'BroadcastChannel' in window,
  getScreenDetails: typeof window !== 'undefined' && 'getScreenDetails' in window,
  isExtended: typeof window !== 'undefined' && 'screen' in window && 'isExtended' in window.screen ? window.screen.isExtended : null,
});

export const requestScreenDetails = async () => {
  if (typeof window === 'undefined' || !('getScreenDetails' in window)) return null;
  const details = await window.getScreenDetails();
  const screens = uniqueScreens(Array.from(details.screens || []).map(describeScreen));
  return {
    currentScreen: details.currentScreen ? describeScreen(details.currentScreen) : null,
    screens,
  };
};

export const buildMonitorUrl = ({ studyInstanceUID, workspaceId, monitorId, role, launchIntent = null, extraParams = null }) => {
  const intent = launchIntent || parseLaunchIntent();
  const url = new URL(`/study/${encodeURIComponent(studyInstanceUID)}`, window.location.origin);
  url.searchParams.set('workspace', workspaceId || 'default');
  url.searchParams.set('monitor', monitorId || 'diagnostic-2');
  url.searchParams.set('role', role || 'comparison');
  url.searchParams.set('display', intent.display || 'clinical-fullscreen');
  if (intent.profileId) url.searchParams.set('launchProfile', intent.profileId);
  if (intent.fullscreen) url.searchParams.set('fullscreen', '1');
  if (extraParams) {
    const params = extraParams instanceof URLSearchParams ? extraParams : new URLSearchParams(extraParams);
    params.forEach((value, key) => url.searchParams.set(key, value));
  }
  return url.toString();
};

const finiteNumber = (value) => Number.isFinite(Number(value)) ? Math.round(Number(value)) : null;

const hasScreenBounds = (descriptor = {}) => (
  finiteNumber(descriptor.left) !== null
  && finiteNumber(descriptor.top) !== null
  && finiteNumber(descriptor.width) !== null
  && finiteNumber(descriptor.height) !== null
);

export const placementFromMonitor = (monitor = {}) => {
  const placement = monitor.placement || {};
  const descriptor = monitor.descriptor || {};
  const useScreenBounds = hasScreenBounds(descriptor);
  const left = finiteNumber(useScreenBounds ? descriptor.left : (placement.left ?? descriptor.availLeft ?? descriptor.left));
  const top = finiteNumber(useScreenBounds ? descriptor.top : (placement.top ?? descriptor.availTop ?? descriptor.top));
  const width = finiteNumber(useScreenBounds ? descriptor.width : (placement.width ?? descriptor.availWidth ?? descriptor.width));
  const height = finiteNumber(useScreenBounds ? descriptor.height : (placement.height ?? descriptor.availHeight ?? descriptor.height));
  const features = {};
  if (left !== null) features.left = left;
  if (top !== null) features.top = top;
  if (width !== null) features.width = width;
  if (height !== null) features.height = height;
  return features;
};

const WINDOW_CHROME_FEATURES = {
  popup: 'yes',
  noopener: 'no',
  toolbar: 'no',
  location: 'no',
  status: 'no',
  menubar: 'no',
  scrollbars: 'no',
  resizable: 'yes',
  fullscreen: 'yes',
};

const applyBestEffortPlacement = (child, placementFeatures = {}) => {
  if (!child) return;
  const left = finiteNumber(placementFeatures.left);
  const top = finiteNumber(placementFeatures.top);
  const width = finiteNumber(placementFeatures.width);
  const height = finiteNumber(placementFeatures.height);
  const place = () => {
    try {
      if (left !== null && top !== null && typeof child.moveTo === 'function') child.moveTo(left, top);
      if (width !== null && height !== null && typeof child.resizeTo === 'function') child.resizeTo(width, height);
      if (typeof child.focus === 'function') child.focus();
    } catch (_) {}
  };
  place();
  window.setTimeout(place, 150);
  window.setTimeout(place, 600);
};

export const applyMonitorPlacement = (targetWindow, monitor = null) => {
  if (!targetWindow || !monitor) return;
  applyBestEffortPlacement(targetWindow, placementFromMonitor(monitor));
};

export const isCurrentWindowTarget = (monitor, currentMonitorId, currentRole) => (
  monitor?.id === currentMonitorId
  || (monitor?.descriptor?.isPrimary && currentMonitorId === 'primary')
  || (monitor?.role === 'primary' && currentMonitorId === 'primary')
  || (monitor?.role === currentRole && currentRole === 'primary')
);

export const createMonitorWindowHandle = ({ workspaceId, monitorId, monitor = null, features = {}, deferPlacement = false }) => {
  const placementFeatures = monitor ? placementFromMonitor(monitor) : {};
  const featureString = Object.entries({ ...WINDOW_CHROME_FEATURES, ...placementFeatures, ...features })
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${key}=${value}`)
    .join(',');
  const child = window.open('about:blank', `kairos-${workspaceId || 'default'}-${monitorId || 'monitor'}`, featureString);
  const applyPlacement = () => applyBestEffortPlacement(child, placementFeatures);
  if (!deferPlacement) applyPlacement();
  return {
    child,
    applyPlacement,
    navigate: (studyInstanceUID, role, extraParams = null) => {
      if (!child || !studyInstanceUID) return false;
      const url = buildMonitorUrl({ studyInstanceUID, workspaceId, monitorId, role, extraParams });
      try { child.location.replace(url); } catch (_) { child.location.href = url; }
      applyPlacement();
      return true;
    },
  };
};

export const openMonitorWindow = ({ studyInstanceUID, workspaceId, monitorId, role, monitor = null, features = {} }) => {
  const handle = createMonitorWindowHandle({ workspaceId, monitorId, monitor, features });
  const child = handle.child;
  if (child) {
    handle.navigate(studyInstanceUID, role);
  }
  return child;
};

export const launchSavedMonitorWorkspace = async ({
  profile,
  studyInstanceUID,
  workspaceId = 'default',
  monitorId = 'primary',
  role = 'primary',
  resolveStudyForRole = async () => ({ studyUID: studyInstanceUID, message: '' }),
  targetWindow = window,
}) => {
  const targets = (profile?.monitors || []).filter(m => m.role !== 'disabled');
  if (!targets.length) {
    return { opened: 0, reusedCurrent: 0, skipped: ['No enabled monitors are saved in this profile yet.'] };
  }

  let reusedCurrent = 0;
  let opened = 0;
  const skipped = [];
  const currentTarget = targets.find(m => isCurrentWindowTarget(m, monitorId, role));
  const launchTargets = currentTarget ? targets.filter(target => target.id !== currentTarget.id) : targets;
  const reservedWindows = launchTargets.map(target => ({
    target,
    handle: createMonitorWindowHandle({ workspaceId, monitorId: target.id, monitor: target, deferPlacement: true }),
  }));

  if (currentTarget) {
    applyMonitorPlacement(targetWindow, currentTarget);
    reusedCurrent = 1;
  }

  for (const { target, handle } of reservedWindows) {
    if (!handle.child) {
      skipped.push(`${target.label || target.id} was blocked by the browser pop-up control.`);
      continue;
    }
    const targetRole = target.role || 'comparison';
    const resolved = await resolveStudyForRole(targetRole);
    if (!resolved.studyUID) {
      try { handle.child.close(); } catch (_) {}
      skipped.push(resolved.message);
      continue;
    }
    if (handle.navigate(resolved.studyUID, targetRole, profile?.launchIntent || null)) opened += 1;
    else skipped.push(`${target.label || target.id} could not be opened.`);
  }

  return { opened, reusedCurrent, skipped };
};

export const useMultiMonitorSync = ({ workspaceId, monitorId, role, onEvent }) => {
  const windowIdRef = React.useRef(getWindowId());
  const channelRef = React.useRef(null);
  const onEventRef = React.useRef(onEvent);
  React.useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return undefined;
    const channel = new BroadcastChannel(`${MULTI_MONITOR_CHANNEL_PREFIX}.${workspaceId || 'default'}`);
    channelRef.current = channel;
    channel.onmessage = (message) => {
      const envelope = message.data;
      if (!envelope || envelope.originWindowId === windowIdRef.current) return;
      if (typeof onEventRef.current === 'function') onEventRef.current(envelope);
    };
    return () => {
      channel.close();
      if (channelRef.current === channel) channelRef.current = null;
    };
  }, [workspaceId]);

  const publish = React.useCallback((type, payload = {}) => {
    const envelope = {
      v: 1,
      type,
      payload,
      workspaceId: workspaceId || 'default',
      sourceMonitorId: monitorId || 'primary',
      sourceRole: role || 'primary',
      originWindowId: windowIdRef.current,
      ts: Date.now(),
    };
    if (channelRef.current) channelRef.current.postMessage(envelope);
    return envelope;
  }, [workspaceId, monitorId, role]);

  return { publish, windowId: windowIdRef.current };
};
